import {useEffect, useRef, useState} from 'react';
import {Button, Collapse, ColorPicker, Radio, Space, Spin} from 'antd';
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import Viewer from './Viewer';
import jsonOptions from './config/options_opt.json';

const defaultSelectedOptions = jsonOptions.reduce((acc, o) => {
  acc[o.name] = o.options.find((opt) => opt.default).name;
  return acc;
}, {});

const whiteColor = '#ffffff';

export const BabylonViewer = () => {
  const canvas = useRef(null);
  const [isLoadModel, setIsLoadModel] = useState(false);
  const viewer = useRef(null);
  const [selectedOptions, setSelectedOptions] = useState(defaultSelectedOptions);
  const enabled = useFeatureIsOn("pricing");

  console.log(enabled);

  useEffect(() => {
    if (!viewer.current) {
      viewer.current = new Viewer(canvas.current, {
        showDebugger: false,
        onLoad: () => {
          setIsLoadModel(true);
        },
        onLoadMaterials: () => {
          jsonOptions.forEach((o) => {
            const selectedOption = o.options.find((opt) => opt.default);
            changeOption(selectedOption, o.ids, o.options);
          });
        }
      });
      viewer.current.setObjectSelector(false);
    }
  }, []);

  const setMaterial = (ids, option, selectedMaterial) => {
    if(option.material) {
      viewer.current.setMaterialForIds(ids, option.material)
    }
    if(option.defaultColor) {
      viewer.current.setColorForIds(ids, option.defaultColor)
    }
    if(option.materials && !selectedMaterial) {
      let selectedIds = option.ids;
      if(option.options) {
        selectedIds = selectedIds.filter(id => !Object.values(option.options).flatMap(o => o.ids).includes(id))
      }
      viewer.current.setMaterialForIds(selectedIds, Object.values(option.materials)[0])
    }
    if(option.materials && selectedMaterial) {
      let selectedIds = option.ids;
      if(option.options) {
        selectedIds = selectedIds.filter(id => !Object.values(option.options).flatMap(o => o.ids).includes(id))
      }
      viewer.current.setMaterialForIds(selectedIds, selectedMaterial)
    }
  }

  const displayColorPicker = (option, ids) => {
    if(option.multicolor) {
      const properties = {}
      if(option.defaultColor) {
        properties.defaultValue = option.defaultColor
      }
      return (
        <ColorPicker
          allowClear
          disabledAlpha
          onClear={() => viewer.current.setColorForIds(ids, whiteColor)}
          onChange={(e) => viewer.current.setColorForIds(ids, e.toHexString())}
          {...properties}
        />
      )
    }
    return null
  }

  const displayMaterials = (option, ids, configName) => {
    if(option.materials && option.name === selectedOptions[configName]) {
      const mats = Object.entries(option.materials).map(([name, material]) => {
        return (
          <Radio.Button key={name} value={material}>{name}</Radio.Button>
        )
      })
      return (
        <Radio.Group
          onChange={(e) => {
            const selectedMaterial = e.target.value;
            changeOption(option, ids, jsonOptions.find(o => o.name === configName).options, selectedMaterial);
          }}
          defaultValue={Object.values(option.materials)[0]}
          buttonStyle="solid"
          size="small"
        >
          {mats}
        </Radio.Group>
      )
    }
    return null
  }

  const displaySubOptions = (option, ids, configName) => {
    if(option.options && option.name === selectedOptions[configName]) {
      const mats = Object.entries(option.options).map(([name, conf]) => {
        return (
          <Radio.Button key={name} value={name}>{name}</Radio.Button>
        )
      })
      const defaultOption = Object.entries(option.options).find(([, conf]) => conf.default)[0]
      return (
        <Radio.Group
          onChange={(e) => {
            const selectedOptionName = e.target.value;
            const selectedOption = option.options[selectedOptionName];
            viewer.current.setMaterialForIds(selectedOption.ids, selectedOption.material)
            viewer.current.setColorForIds(selectedOption.ids, selectedOption.defaultColor)
          }}
          defaultValue={defaultOption}
          buttonStyle="solid"
          size="small"
        >
          {mats}
        </Radio.Group>
      )
    }
    return null
  }

  const showHideMeshes = (selectedOption, ids, allOptions) => {
    const otherOptions = allOptions.filter(o => o.name !== selectedOption.name)
    if(selectedOption.ids) {
      viewer.current.showMeshes(selectedOption.ids)
      viewer.current.hideMeshes(ids)
      otherOptions.forEach((opt) => {
        if(opt.ids) {
          viewer.current.hideMeshes(opt.ids)
        }
      })
    } else {
      viewer.current.showMeshes(ids)
      otherOptions.forEach((opt) => {
        if(opt.ids) {
          viewer.current.hideMeshes(opt.ids)
        }
      })
    }
  }

  const changeOption = (selectedOption, ids, allOptions, selectedMaterial) => {
    setMaterial(ids, selectedOption, selectedMaterial);
    showHideMeshes(selectedOption, ids, allOptions);
  }

  const optionsList = jsonOptions.filter(o => !o.hidden).map((o, i) => {
    const selectedValue = o.options.find((opt) => opt.name === selectedOptions[o.name]).name;
    return (
      {
        key: i,
        label: o.name,
        children:
                  (
                    <Radio.Group
                      value={selectedValue}
                      onChange={(e) => {
                        const selectedOptionName = e.target.value;
                        const selectedOption = o.options.find((opt) => opt.name === selectedOptionName);
                        changeOption(selectedOption, o.ids, o.options);

                        if(selectedOption.options) {
                          const defaultSubOption = Object.values(selectedOption.options).find((conf) => conf.default)
                          viewer.current.setMaterialForIds(defaultSubOption.ids, defaultSubOption.material)
                          viewer.current.setColorForIds(defaultSubOption.ids, defaultSubOption.defaultColor)
                        }
                        setSelectedOptions({ ...selectedOptions, [o.name]: selectedOptionName });
                      }}
                    >
                      <Space direction="vertical">
                        {o.options.map((opt) => (
                          <>
                            <Radio key={opt.name} value={opt.name}>
                              <div>{opt.name}</div>
                              {displayColorPicker(opt, o.ids)}
                            </Radio>
                            {displayMaterials(opt, o.ids, o.name)}
                            {displaySubOptions(opt, o.ids, o.name)}
                          </>
                        ))}
                      </Space>
                    </Radio.Group>
                  ),
      }
    );
  });

  return (
    <div style={{ display: 'flex', height: '100%' }}>
      {!isLoadModel
                && (
                  <div style={{ position: 'absolute', top: '50%', left: '50%' }}>
                    <Spin size="large" />
                  </div>
                )}
      <canvas ref={canvas} style={{ width: '70%' }} />
      <div style={{
        width: '30%', padding: '0.5rem', overflowY: 'auto',
      }}
      >
        <h3>Configurations</h3>
        <Collapse accordion items={optionsList} />
      </div>
    </div>
  );
};
